// Bringing a removed condition line back (#509; DD, 2026-10-23): "+ Add
// surface" offers it, and its rating record is made active again with its old
// rating, defects and note. A removed line keeps its rating through a scale
// change (#473), and a rating made before a scale change must be rated again,
// so only a rating known to be made on the report's scale now comes back. Pure.
import { CLEARED, RATED_ON, staleRatings } from './condition.ts';
import { ACTIVE_STATE, nameChange, type RatingLine } from './condition-lines.ts';
import type { DeviceRecord, Fields } from './device-db';

/**
 * What bringing a removed line back saves: the row active again, under the
 * line's name now (a surface renamed on the Measure tab while it was removed),
 * and its rating — sent again when it holds, so the server can still clear it
 * if another iPad changed the scale meanwhile (#473), cleared when it doesn't.
 */
export function restoreChanges(observation: DeviceRecord, line: Pick<RatingLine, 'name'>, visitScaleLabel: unknown): Fields {
  const rating = ratingStillHolds(observation, visitScaleLabel) ? { hig_rating: observation.fields.hig_rating ?? null } : CLEARED;
  return { statecode: ACTIVE_STATE, ...nameChange(observation, line), ...rating };
}

const hasRating = (o: DeviceRecord): boolean => o.fields.hig_rating !== null && o.fields.hig_rating !== undefined && o.fields.hig_rating !== '';

/**
 * Whether a removed line's rating comes back with it. Only a rating stamped
 * with the scale it was made on can show it was made on the visit's scale
 * (checked as a scale change checks any other, staleRatings). The stamp is the
 * device's own, so CRM's copy has none: its scale can't be known, and it comes
 * back unrated.
 */
export function ratingStillHolds(observation: DeviceRecord, visitScaleLabel: unknown): boolean {
  if (!hasRating(observation)) return true;
  if (typeof observation.fields[RATED_ON] !== 'string') return false;
  const asOnReport = { ...observation, fields: { ...observation.fields, statecode: 'Active' } };
  return staleRatings([asOnReport], visitScaleLabel).length === 0;
}
